import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, TextField, Autocomplete, Chip, useMediaQuery,
} from '@mui/material';
import theme from '../../util/theme/theme';
import BottomSheetDialog from './mobile/mobile-bottom-sheet';

type Country = { id: number; country: string };
type Entity = { id: number; name: string };

type FeatureFlagOption = { key: string; label: string };

// All known feature keys in the codebase — add new ones here as they're built
export const KNOWN_FEATURE_FLAGS: FeatureFlagOption[] = [
    { key: 'gst', label: 'GST' },
    { key: 'invoice_email_sending', label: 'Invoice Email Sending' },
];

type FeatureFlagModalProps = {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: { featureKey: string; label: string; allowedCountryIds: number[]; excludedEntityIds: number[]; isActive: boolean }) => void;
    isEdit?: boolean;
    editData?: { id: number; featureKey: string; label: string; allowedCountryIds: number[]; excludedEntityIds: number[]; isActive: boolean } | null;
    countries: Country[];
    entities: Entity[];
    existingKeys: string[];
};

const AddFeatureFlagModal: React.FC<FeatureFlagModalProps> = ({
    open, onClose, onSubmit, isEdit = false, editData, countries, entities, existingKeys,
}) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [selectedFlag, setSelectedFlag] = useState<FeatureFlagOption | null>(null);
    const [label, setLabel] = useState('');
    const [selectedCountries, setSelectedCountries] = useState<Country[]>([]);
    const [excludedEntities, setExcludedEntities] = useState<Entity[]>([]);
    const [errors, setErrors] = useState<{ featureKey?: string; label?: string }>({});

    const availableFlags = KNOWN_FEATURE_FLAGS.filter(f => !existingKeys.includes(f.key));

    useEffect(() => {
        setSelectedFlag(null);
        setLabel('');
        setSelectedCountries([]);
        setExcludedEntities([]);
        setErrors({});

        if (isEdit && editData) {
            setSelectedFlag(KNOWN_FEATURE_FLAGS.find(f => f.key === editData.featureKey) ?? { key: editData.featureKey, label: editData.label });
            setLabel(editData.label);
            setSelectedCountries(countries.filter(c => editData.allowedCountryIds.includes(c.id)));
            setExcludedEntities(entities.filter(e => editData.excludedEntityIds.includes(e.id)));
        }
    }, [isEdit, editData, open, countries, entities]);

    const handleFlagSelect = (flag: FeatureFlagOption | null) => {
        setSelectedFlag(flag);
        if (flag) setLabel(flag.label);
    };

    const handleSubmit = () => {
        const newErrors: { featureKey?: string; label?: string } = {};
        if (!selectedFlag) newErrors.featureKey = 'Feature is required';
        if (!label.trim()) newErrors.label = 'Label is required';
        if (Object.keys(newErrors).length) { setErrors(newErrors); return; }

        onSubmit({
            featureKey: selectedFlag!.key,
            label: label.trim(),
            allowedCountryIds: selectedCountries.map(c => c.id),
            excludedEntityIds: excludedEntities.map(e => e.id),
            isActive: editData?.isActive ?? true,
        });
    };

    const handleClose = () => {
        setSelectedFlag(null);
        setLabel('');
        setSelectedCountries([]);
        setExcludedEntities([]);
        setErrors({});
        onClose();
    };

    const content = (
        <>
            <DialogTitle variant="h6" fontWeight={600}>
                {isEdit ? 'Edit Feature Flag' : 'Add Feature Flag'}
            </DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={3} mt={1}>
                    <Box>
                        <Typography mb={1} fontSize={14} color="var(--color-text-muted)">
                            Feature <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        <Autocomplete
                            options={isEdit ? KNOWN_FEATURE_FLAGS : availableFlags}
                            getOptionLabel={f => `${f.label} (${f.key})`}
                            value={selectedFlag}
                            onChange={(_, val) => handleFlagSelect(val)}
                            disabled={isEdit}
                            isOptionEqualToValue={(a, b) => a.key === b.key}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    size="small"
                                    placeholder="Select a feature…"
                                    error={!!errors.featureKey}
                                    helperText={errors.featureKey}
                                />
                            )}
                            noOptionsText="All features have been configured"
                        />
                    </Box>
                    <Box>
                        <Typography mb={1} fontSize={14} color="var(--color-text-muted)">
                            Label <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        <TextField
                            value={label}
                            onChange={e => setLabel(e.target.value)}
                            size="small"
                            fullWidth
                            placeholder="Display name"
                            error={!!errors.label}
                            helperText={errors.label}
                        />
                    </Box>
                    <Box>
                        <Typography mb={1} fontSize={14} color="var(--color-text-muted)">
                            Allowed Countries
                        </Typography>
                        <Typography mb={1} fontSize={12} color="text.secondary">
                            Leave empty to make this feature available in all countries.
                        </Typography>
                        <Autocomplete
                            multiple
                            options={countries}
                            getOptionLabel={c => c.country}
                            value={selectedCountries}
                            onChange={(_, val) => setSelectedCountries(val)}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            renderTags={(val, getTagProps) =>
                                val.map((c, i) => (
                                    <Chip label={c.country} size="small" {...getTagProps({ index: i })} />
                                ))
                            }
                            renderInput={params => (
                                <TextField {...params} size="small" placeholder="Select countries…" />
                            )}
                        />
                    </Box>
                    <Box>
                        <Typography mb={1} fontSize={14} color="var(--color-text-muted)">
                            Excluded Entities
                        </Typography>
                        <Typography mb={1} fontSize={12} color="text.secondary">
                            These entities will never see this feature, regardless of country.
                        </Typography>
                        <Autocomplete
                            multiple
                            options={entities}
                            getOptionLabel={e => e.name}
                            value={excludedEntities}
                            onChange={(_, val) => setExcludedEntities(val)}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            renderTags={(val, getTagProps) =>
                                val.map((e, i) => (
                                    <Chip label={e.name} size="small" {...getTagProps({ index: i })} />
                                ))
                            }
                            renderInput={params => (
                                <TextField {...params} size="small" placeholder="Select entities to exclude…" />
                            )}
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                <Button
                    onClick={handleClose}
                    variant="contained"
                    sx={{ background: 'var(--color-container-background)', color: 'var(--color-default-text)', borderRadius: 2, fontWeight: 600, boxShadow: 'none', textTransform: 'none' }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    sx={{ background: 'var(--color-primary)', color: '#fff', borderRadius: 2, fontWeight: 600, boxShadow: 'none', textTransform: 'none' }}
                >
                    {isEdit ? 'Save Changes' : 'Add Feature Flag'}
                </Button>
            </DialogActions>
        </>
    );

    return isMobile ? (
        <BottomSheetDialog open={open} onClose={handleClose} heightVh={90}>
            {content}
        </BottomSheetDialog>
    ) : (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth sx={{ '& .MuiPaper-root': { borderRadius: 3 } }}>
            {content}
        </Dialog>
    );
};

export default AddFeatureFlagModal;
